import { GovernanceColors } from '@/config/governanceColors';
import { useDiscoveryData } from '@/hooks/useCommandCenter';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, BarChart3, AlertCircle, Scan } from 'lucide-react';

export function DiscoveryView() {
  const { data, loading } = useDiscoveryData();

  if (loading) {
    return <div className="text-[#A8A29E] text-sm">Loading discovery data...</div>;
  }

  const hasSignalAggregator = data?.signalAggregator ?? data?.organizationalDiscovery ?? false;

  // The /api/cc/reports/preview endpoint returns:
  //   { eventCount, agentCount, sentinelEvents, overrideEvents, quarantineEvents, gaps: [] }
  // Read those field names (older aliases kept as fallbacks). Previously this
  // view read auditEvents/agentsDetected/securityEvents -- names the endpoint
  // never emits -- so every tile rendered 0 despite live data being present.
  const frameworks = data?.frameworks ?? 9;
  const auditEvents = data?.eventCount ?? data?.auditEvents ?? data?.totalEvents ?? 0;
  const agentsDetected = data?.agentCount ?? data?.agentsDetected ?? 0;
  const securityEvents = data?.sentinelEvents ?? data?.securityEvents ?? data?.security ?? 0;
  const overrideEvents = data?.overrideEvents ?? data?.overrides ?? 0;
  const quarantineEvents = data?.quarantineEvents ?? data?.quarantines ?? 0;
  const rawGaps = data?.gaps ?? data?.gapsDetected ?? 0;
  const gaps = Array.isArray(rawGaps) ? rawGaps.length : rawGaps;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Discovery</h2>
        <Button
          variant="outline"
          size="sm"
          disabled
          className="border-[#4A443D] text-[#8F857B] cursor-not-allowed"
        >
          <Scan className="w-3 h-3 mr-2" />
          Run Scan (Next Release)
        </Button>
      </div>

      <div className="flex gap-3">
        <Badge variant="outline" style={{ color: GovernanceColors.streaming, borderColor: GovernanceColors.streaming }}>
          {frameworks} Framework Signatures
        </Badge>
      </div>

      {!hasSignalAggregator && (
        <div className="text-[#A8A29E] text-sm p-4 bg-[#292524] border border-[#3A352F] rounded-lg">
          <p className="font-medium text-[#C9D1D9] mb-2">Local discovery active</p>
          <p>Showing local agent discovery. Connect a signal aggregator for organizational discovery across DNS, MDM, and behavioral signals.</p>
          <p className="mt-2 text-xs">Set SIGNAL_AGGREGATOR_URL in your environment to enable.</p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Search className="w-4 h-4" style={{ color: GovernanceColors.primary }} />
              <span className="text-sm font-medium">Discovery Stats</span>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-[#A8A29E]">Audit events: <span className="text-[#F5F0E8]">{auditEvents}</span></p>
              <p className="text-xs text-[#A8A29E]">Agents detected: <span className="text-[#F5F0E8]">{agentsDetected}</span></p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4" style={{ color: GovernanceColors.warning }} />
              <span className="text-sm font-medium">Event Breakdown</span>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-[#A8A29E]">Security: <span className="text-[#F5F0E8]">{securityEvents}</span></p>
              <p className="text-xs text-[#A8A29E]">Override: <span className="text-[#F5F0E8]">{overrideEvents}</span></p>
              <p className="text-xs text-[#A8A29E]">Quarantine: <span className="text-[#F5F0E8]">{quarantineEvents}</span></p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4" style={{ color: gaps > 0 ? GovernanceColors.warning : GovernanceColors.healthy }} />
              <span className="text-sm font-medium">Gaps Detected</span>
            </div>
            <span className="text-2xl font-bold" style={{ color: gaps > 0 ? GovernanceColors.warning : GovernanceColors.healthy }}>{gaps}</span>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
